// Secondary library folder field: editable relative path + optional browse
import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  Check,
  Folder,
  FolderOpen,
  Refresh,
  Search,
} from '@mui/icons-material';
import { apiService, SecondaryLibraryFolder } from '../../services/api';
import { canonicalizeFolderPath } from '../../utils/sharePointFolderPath';

export interface SecondaryLibraryFolderFieldProps {
  libraryId: number;
  libraryDisplayName: string;
  value: string | null;
  onChange: (path: string, folderPathMode: 'library_relative') => void;
  disabled?: boolean;
  label?: string;
}

const SecondaryLibraryFolderField = ({
  libraryId,
  libraryDisplayName,
  value,
  onChange,
  disabled = false,
  label = 'Target Folder',
}: SecondaryLibraryFolderFieldProps) => {
  const [pathText, setPathText] = useState(value || '');
  const [browseOpen, setBrowseOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [browseError, setBrowseError] = useState<string | null>(null);
  const [folders, setFolders] = useState<SecondaryLibraryFolder[]>([]);
  const [currentSegments, setCurrentSegments] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [appendText, setAppendText] = useState('');

  useEffect(() => {
    setPathText(value || '');
  }, [value, libraryId]);

  const validation = canonicalizeFolderPath(pathText, 'library_relative');
  const previewPath = validation.normalized || pathText.trim() || '(enter a folder path)';

  const loadFolders = useCallback(async () => {
    setLoading(true);
    setBrowseError(null);
    try {
      const parentPath = currentSegments.join('/');
      const response = await apiService.listSecondaryLibraryFolders(libraryId, parentPath || undefined);
      setFolders(response.folders || []);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string; code?: string } }; message?: string };
      const message =
        axiosErr.response?.data?.message
        || axiosErr.message
        || 'Failed to load folders';
      setBrowseError(message);
      setFolders([]);
    } finally {
      setLoading(false);
    }
  }, [libraryId, currentSegments]);

  useEffect(() => {
    if (browseOpen) {
      loadFolders();
    }
  }, [browseOpen, loadFolders]);

  // Reset browse path when library changes
  useEffect(() => {
    setCurrentSegments([]);
    setFolders([]);
    setBrowseError(null);
    setSearchTerm('');
  }, [libraryId]);

  const commitPath = (raw: string) => {
    const check = canonicalizeFolderPath(raw, 'library_relative');
    const toStore = check.valid && check.normalized != null ? check.normalized : raw.trim();
    setPathText(toStore);
    onChange(toStore, 'library_relative');
  };

  const handleSelectFolder = (relativePath: string) => {
    const withAppend = appendText.trim()
      ? `${relativePath}/${appendText.trim().replace(/^\/+|\/+$/g, '').replace(/\\/g, '/')}`
      : relativePath;
    commitPath(withAppend);
    setBrowseOpen(false);
    setAppendText('');
  };

  const filtered = folders.filter((f) =>
    !searchTerm || f.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ mb: 1 }}>
      <TextField
        label={label}
        value={pathText}
        onChange={(e) => {
          setPathText(e.target.value);
          onChange(e.target.value, 'library_relative');
        }}
        onBlur={() => commitPath(pathText)}
        disabled={disabled}
        fullWidth
        required
        error={Boolean(pathText.trim()) && !validation.valid}
        helperText={
          pathText.trim() && !validation.valid
            ? validation.message
            : 'Relative to the selected library root. Type a path or browse existing folders.'
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Button
                size="small"
                onClick={() => setBrowseOpen(true)}
                disabled={disabled}
              >
                Browse
              </Button>
            </InputAdornment>
          ),
        }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Destination: <strong>{libraryDisplayName}</strong>
        {' / '}
        {previewPath}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        Missing folder segments will be created when the document is generated. Typed paths are not confirmed to exist until then.
      </Typography>

      <Dialog open={browseOpen} onClose={() => setBrowseOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Browse folders — {libraryDisplayName}
          <IconButton onClick={loadFolders} disabled={loading} aria-label="Refresh folders">
            <Refresh />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Breadcrumbs sx={{ mb: 1 }}>
            <Link
              component="button"
              underline="hover"
              color="inherit"
              onClick={() => setCurrentSegments([])}
            >
              Library root
            </Link>
            {currentSegments.map((seg, idx) => (
              <Link
                key={`${seg}-${idx}`}
                component="button"
                underline="hover"
                color={idx === currentSegments.length - 1 ? 'text.primary' : 'inherit'}
                onClick={() => setCurrentSegments(currentSegments.slice(0, idx + 1))}
              >
                {seg}
              </Link>
            ))}
          </Breadcrumbs>

          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            {currentSegments.length > 0 && (
              <Button
                startIcon={<ArrowBack />}
                size="small"
                onClick={() => setCurrentSegments(currentSegments.slice(0, -1))}
              >
                Up
              </Button>
            )}
            <TextField
              size="small"
              placeholder="Search folders"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {browseError && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {browseError}. You can still type a folder path in the field above.
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <List dense sx={{ maxHeight: 280, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              {filtered.length === 0 && !browseError && (
                <ListItem>
                  <ListItemText primary="No folders at this level" secondary="Type a new path or append segments below" />
                </ListItem>
              )}
              {filtered.map((folder) => (
                <ListItem key={folder.id} disablePadding secondaryAction={
                  <Button size="small" startIcon={<Check />} onClick={() => handleSelectFolder(folder.relativePath)}>
                    Select
                  </Button>
                }>
                  <ListItemButton onClick={() => setCurrentSegments(folder.relativePath.split('/'))}>
                    <ListItemIcon>
                      {folder.childCount && folder.childCount > 0 ? <FolderOpen /> : <Folder />}
                    </ListItemIcon>
                    <ListItemText primary={folder.name} secondary={folder.relativePath} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}

          <TextField
            label="Append new subfolder(s)"
            value={appendText}
            onChange={(e) => setAppendText(e.target.value)}
            fullWidth
            size="small"
            sx={{ mt: 2 }}
            helperText="Optional. Example: Approved/2026 — applied when you Select a folder"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBrowseOpen(false)}>Close</Button>
          {currentSegments.length > 0 && (
            <Button
              variant="contained"
              onClick={() => handleSelectFolder(currentSegments.join('/'))}
            >
              Use current folder
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SecondaryLibraryFolderField;
